import { useEffect, useState } from "react";
import { ArrowLeft, Eye, EyeOff, LockKeyhole, PackageSearch, ShieldCheck } from "lucide-react";
import { ACCOUNTS_SESSION, loadIdentitySession, loadAdminTradeLedger, loadCustomerOrders, loginAdmin, loginCustomer } from "./api";
import { TradeLedgerPanel } from "./TradeLedger";
import { LoadingOverlay } from "./LoadingOverlay";
import { PullToRefresh } from "./PullToRefresh";
import { ADMIN_LOGIN_PATH, initialAccessMode, shouldStartCentralLogin } from "./accessNavigation";
import type { CustomerOrder } from "./types";

const SESSION_TOKEN_KEY = "yoyo-access-session-token";
const SESSION_ROLE_KEY = "yoyo-access-session-role";
const ADMIN_PERSISTED_SESSION_KEY = "yoyo-admin-persisted-session";
const ADMIN_SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000;
type AccessRole = "admin" | "customer";

type PersistedAdminSession = {
  accessToken: string;
  role: "admin";
  expiresAt: number;
};

function readPersistedAdminSession(): PersistedAdminSession | null {
  const raw = localStorage.getItem(ADMIN_PERSISTED_SESSION_KEY);
  if (!raw) {
    return null;
  }
  try {
    const session = JSON.parse(raw) as Partial<PersistedAdminSession>;
    if (
      session.role !== "admin" ||
      typeof session.accessToken !== "string" ||
      !session.accessToken ||
      typeof session.expiresAt !== "number" ||
      session.expiresAt <= Date.now()
    ) {
      localStorage.removeItem(ADMIN_PERSISTED_SESSION_KEY);
      return null;
    }
    return session as PersistedAdminSession;
  } catch {
    localStorage.removeItem(ADMIN_PERSISTED_SESSION_KEY);
    return null;
  }
}

function getInitialAccessSession(): { token: string; role: AccessRole | null } {
  if (initialAccessMode(window.location.search) === "customer") {
    const token = sessionStorage.getItem(SESSION_TOKEN_KEY) ?? "";
    return { token, role: sessionStorage.getItem(SESSION_ROLE_KEY) === "customer" ? "customer" : null };
  }
  const persistedAdmin = readPersistedAdminSession();
  if (persistedAdmin) {
    return { token: persistedAdmin.accessToken, role: persistedAdmin.role as AccessRole };
  }
  const legacyToken = sessionStorage.getItem("yoyo-admin-session-token") ?? "";
  const token = sessionStorage.getItem(SESSION_TOKEN_KEY) ?? legacyToken;
  const savedRole = sessionStorage.getItem(SESSION_ROLE_KEY);
  const role = savedRole === "admin" || legacyToken ? "admin" : null;
  return { token: role ? token : "", role };
}

export function clearAdminSession() {
  sessionStorage.removeItem(SESSION_TOKEN_KEY);
  sessionStorage.removeItem(SESSION_ROLE_KEY);
  sessionStorage.removeItem("yoyo-admin-session-token");
  localStorage.removeItem(ADMIN_PERSISTED_SESSION_KEY);
  localStorage.removeItem("yoyo-admin-token");
}

export function AdminPortal() {
  const [initialSession] = useState(getInitialAccessSession);
  const [token, setToken] = useState(initialSession.token);
  const [role, setRole] = useState<AccessRole | null>(initialSession.role);
  const [mode] = useState<AccessRole>(() => initialAccessMode(window.location.search));
  const [showPassword, setShowPassword] = useState(false);
  const [provider, setProvider] = useState<"local" | "accounts" | "customer" | null>(null);
  const [retry, setRetry] = useState(0);
  const [identityMessage, setIdentityMessage] = useState("");
  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [status, setStatus] = useState<"locked" | "checking" | "ready" | "error">(
    "checking"
  );

  useEffect(() => {
    let cancelled = false;
    setStatus("checking");
    // Customer proof is independent of the administrator's SSO cookie and availability.
    if (mode === "customer") {
      const customerToken = initialSession.role === "customer" ? initialSession.token : "";
      setProvider("customer");
      setToken(customerToken);
      setRole(customerToken ? "customer" : null);
      setStatus(customerToken ? "checking" : "locked");
      return;
    }
    void loadIdentitySession().then(session => {
      if (cancelled) return;
      setProvider(session.provider);
      if (session.provider === "accounts") {
        const url = new URL(window.location.href);
        const callbackError = url.searchParams.get("authError");
        const authError = callbackError || (session.error === "unauthorized" ? undefined : session.error);
        if (url.searchParams.has("authError")) {
          url.searchParams.delete("authError");
          window.history.replaceState(null, "", url);
        }
        // Old browser admin tokens cannot override the central account's live role.
        localStorage.removeItem(ADMIN_PERSISTED_SESSION_KEY);
        localStorage.removeItem("yoyo-admin-token");
        sessionStorage.removeItem("yoyo-admin-session-token");
        if (session.authenticated) {
          setToken(ACCOUNTS_SESSION);
          setRole("admin");
          setStatus("ready");
        } else {
          if (initialSession.role !== "customer") { setToken(""); setRole(null); }
          if (shouldStartCentralLogin(session, mode, initialSession.role === "customer", callbackError)) {
            window.location.replace(ADMIN_LOGIN_PATH);
            return;
          }
          const messages: Record<string, string> = {
            accounts_access_denied: "此账号没有 YooY 管理员权限，请在统一账号中心分配 yoyo 角色。",
            accounts_login_expired: "登录请求已过期，请重新登录。",
            accounts_unavailable: "统一账号服务暂不可用，请稍后重试。",
            accounts_session_expired: "统一账号会话已失效，请重新登录。"
          };
          setIdentityMessage(authError ? messages[authError] || "统一登录未完成，请重试。" : "");
          setStatus("locked");
        }
      } else {
        setStatus(initialSession.token && initialSession.role ? "checking" : "locked");
      }
    }).catch(() => { if (!cancelled) { setIdentityMessage("账号服务暂不可用，请稍后重试。"); setStatus("error"); } });
    return () => { cancelled = true; };
  }, [retry]);

  useEffect(() => {
    if (!provider || !token || !role || token === ACCOUNTS_SESSION) return;
    let cancelled = false;
    setStatus("checking");
    const request = role === "admin" ? loadAdminTradeLedger(token, 1) : loadCustomerOrders(token);
    void request.then((result) => {
      if (cancelled) return;
      if (role === "customer") setOrders(result as CustomerOrder[]);
      setStatus("ready");
    }).catch(() => {
      if (cancelled) return;
      clearAdminSession();
      setToken("");
      setRole(null);
      setStatus("error");
    });
    return () => { cancelled = true; };
  }, [provider, role, token]);

  const isChecking: boolean = status === "checking";

  if (!provider && !isChecking) {
    return <section className="admin-login account-login"><p role="alert">{identityMessage}</p><button className="primary-button" type="button" onClick={() => setRetry(value => value + 1)}>重试</button></section>;
  }

  async function signIn(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    setStatus("checking");
    try {
      const session = mode === "admin"
        ? await loginAdmin(String(formData.get("username") ?? ""), String(formData.get("password") ?? ""))
        : await loginCustomer(String(formData.get("phone") ?? ""), Number(formData.get("orderId")));
      if (session.role === "admin") {
        localStorage.setItem(ADMIN_PERSISTED_SESSION_KEY, JSON.stringify({
          accessToken: session.accessToken,
          role: session.role,
          expiresAt: Date.now() + ADMIN_SESSION_TTL_MS
        } satisfies PersistedAdminSession));
        sessionStorage.removeItem(SESSION_TOKEN_KEY);
        sessionStorage.removeItem(SESSION_ROLE_KEY);
      } else {
        sessionStorage.setItem(SESSION_TOKEN_KEY, session.accessToken);
        sessionStorage.setItem(SESSION_ROLE_KEY, session.role);
      }
      sessionStorage.removeItem("yoyo-admin-session-token");
      localStorage.removeItem("yoyo-admin-token");
      setToken(session.accessToken);
      setRole(session.role);
      form.reset();
    } catch {
      setStatus("error");
    }
  }

  async function refreshCustomerOrders() {
    if (!token || role !== "customer") {
      return;
    }
    const nextOrders = await loadCustomerOrders(token);
    setOrders(nextOrders);
  }

  function resetCustomerQuery() {
    sessionStorage.removeItem(SESSION_TOKEN_KEY);
    sessionStorage.removeItem(SESSION_ROLE_KEY);
    setToken(""); setRole(null); setOrders([]); setStatus("locked");
  }

  if (status === "ready" && token && role === "admin") {
    return <><LoadingOverlay active={false} /><TradeLedgerPanel token={token} onChanged={() => undefined} /></>;
  }
  if (status === "ready" && token && role === "customer") {
    return <><LoadingOverlay active={false} /><PullToRefresh onRefresh={refreshCustomerOrders}><CustomerOrders orders={orders} onReset={resetCustomerQuery} /></PullToRefresh></>;
  }

  return (
    <>
      <LoadingOverlay active={isChecking} label={token && role ? "正在恢复会话" : "正在验证"} />
      {!isChecking && <section className={`admin-login account-login${mode === "customer" ? " customer-access" : ""}`} aria-label={mode === "admin" ? "管理员登录" : "查询我的订单"}>
      {mode === "customer" ? <>
        <a className="customer-back-link" href="/"><ArrowLeft size={14} aria-hidden="true" />返回店面</a>
        <h2>查询订单</h2>
        <p>使用下单手机号和订单号验证后查看。</p>
      </> : provider !== "accounts" && <>
        <div className="admin-login-mark" aria-hidden="true"><LockKeyhole size={24} /></div>
        <h2>管理员登录</h2><p>登录后进入采销与分析。</p>
      </>}
      {mode === "admin" && provider === "accounts" ? <div className="accounts-login-action">
        <p>使用 Sine 账号登录，权限由统一账号中心管理。</p>
        <a className="primary-button" href={ADMIN_LOGIN_PATH}>统一账号登录</a>
        {identityMessage && <p className="admin-login-error" role="alert">{identityMessage}</p>}
      </div> : <form key={mode} onSubmit={signIn}>
        {mode === "customer" ? <>
          <label><span>下单手机号</span><input name="phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="11 位手机号" pattern="1[3-9][0-9]{9}" required /></label>
          <label><span>订单号</span><input name="orderId" type="number" inputMode="numeric" placeholder="订单编号" min="1" required /></label>
        </> : <>
          <label><span>管理员账号</span><input name="username" autoComplete="username" placeholder="输入账号" required /></label>
          <label>
            <span>密码</span>
            <div className="admin-secret-field">
              <input name="password" type={showPassword ? "text" : "password"} autoComplete="current-password" placeholder="输入密码" required />
              <button type="button" onClick={() => setShowPassword((current) => !current)} aria-label={showPassword ? "隐藏密码" : "显示密码"}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button>
            </div>
          </label>
        </>}
        <button className="primary-button" type="submit" disabled={status === "checking"}>
          {status === "checking" ? "正在验证" : mode === "admin" ? "登录管理台" : "查询订单"}
        </button>
      </form>}
      {status === "error" && <p className="admin-login-error" role="alert">{mode === "admin" ? "账号或密码不正确。" : "手机号或订单号不匹配。"}</p>}
      <small><ShieldCheck size={14} aria-hidden="true" />{mode === "admin" ? "此浏览器 7 天内免登录，退出可结束会话" : "仅验证本人订单，关闭浏览器后需重新验证"}</small>
      </section>}
    </>
  );
}

function CustomerOrders({ orders, onReset }: { orders: CustomerOrder[]; onReset: () => void }) {
  return (
    <section className="customer-orders" aria-label="我的订单">
      <header><span>共 {orders.length} 笔订单</span><button type="button" className="text-button" onClick={onReset}>重新查询</button></header>
      <div className="customer-order-list">
        {orders.map((order) => (
          <article key={order.id}>
            {order.product_image ? <img src={order.product_image} alt="" /> : <div className="customer-order-placeholder"><PackageSearch size={20} /></div>}
            <div><small>订单 #{order.id} · {formatDate(order.created_at)}</small><strong>{order.product_title || order.product_id}</strong><span>{order.city} · {translateIntent(order.intent)}</span></div>
            <b className={`customer-order-status ${order.status}`}>{translateOrderStatus(order.status)}</b>
          </article>
        ))}
      </div>
    </section>
  );
}

function formatDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat("zh-CN", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" }).format(date);
}

function translateIntent(value: CustomerOrder["intent"]) {
  return { reserve: "预约看货", buy: "购买", rent: "先租后买" }[value];
}

function translateOrderStatus(value: CustomerOrder["status"]) {
  return { new: "待确认", confirmed: "已确认", paid: "已付款", fulfilled: "已完成", cancelled: "已取消" }[value];
}
